import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import GlobalContext from "../../common/Context";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Container,
  IconButton,
} from "@mui/material";
import * as constants from "../../../constants";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LoginGraphic from "../../common/LoginGraphic/LoginGraphic";
import LoadingModal from "../../common/LoadingModal/LoadingModal";

const SignIn = () => {
  const { mobileView, firebaseService, authError } = useContext(GlobalContext);
  const [textType, setTextType] = useState("password");
  const [loading, setLoading] = useState(false);
  const [signinError, setSigninError] = useState("");

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const container = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: mobileView ? "100%" : "40rem",
  };

  const titleStyle = {
    mb: 4,
    mt: 2,
    textAlign: "center",
    fontSize: "2rem",
  };

  const button = {
    mb: 2,
    width: mobileView ? "100%" : "50%",
  };

  const inputContainer = {
    display: "flex",
    flexDirection: "column",
    mb: 3,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  };

  const inputStyle = {
    "& .MuiInputBase-root": {
      p: 0,
      "& input": { pl: "3.5rem" },
      "& > div": {
        position: "absolute",
        left: "1rem",
      },
      "& > div:nth-of-type(2)": {
        left: "auto",
        right: "1rem",
      },
    },
  };

  const buttonsContainer = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    mt: 3,
    width: "100%",
  };

  const handleIconClick = (type) => {
    type === "password" ? setTextType("text") : setTextType("password");
  };

  useEffect(() => {
    if (authError) {
      setSigninError(constants.SIGNIN_NO_ACCESS);
    }
  }, [authError]);

  const onSubmit = async ({ email, password }) => {
    try {
      setSigninError("");
      setLoading(true);

      await firebaseService.doSignInWithEmailAndPassword(email, password);
    } catch (error) {
      switch (error.code) {
        case "auth/wrong-password":
          setSigninError(constants.SIGNIN_BAD_CREDS);
          break;
        default:
          setSigninError(constants.SIGNIN_NO_ACCESS);
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  return (
    <Container sx={containerStyle}>
      {loading && <LoadingModal width={6} />}
      {<LoginGraphic />}
      <Typography variant="h2" color="primary" sx={titleStyle}>
        {constants.APP_NAME}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: mobileView ? "100%" : null }}>
        <Box sx={container}>
          <Box sx={inputContainer}>
            <TextField
              sx={inputStyle}
              fullWidth
              autoFocus
              label="Email"
              id="email"
              {...register("email", {
                required: constants.FRM_EMAIL_REQUIRED,
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: constants.SIGNIN_BAD_EMAIL,
                },
              })}
              type="email"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment>{<EmailIcon />}</InputAdornment>,
                },
              }}
            />
            {errors.email && (
              <Typography variant="body2" color="#CE0C14" mt={1}>
                {errors.email.message}
              </Typography>
            )}
          </Box>
          <Box sx={inputContainer}>
            <TextField
              sx={inputStyle}
              fullWidth
              label="Password"
              id="password"
              {...register("password", {
                required: constants.FRM_PW_REQUIRED,
              })}
              type={textType}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment>{<LockIcon />}</InputAdornment>,
                  endAdornment: (
                    <InputAdornment>
                      {
                        <IconButton onClick={() => handleIconClick(textType)}>
                          {textType === "password" ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      }
                    </InputAdornment>
                  ),
                },
              }}
            />
            {errors.password && (
              <Typography variant="body2" color="#CE0C14" mt={1}>
                {errors.password.message}
              </Typography>
            )}
            {signinError && (
              <Typography variant="body2" color="#CE0C14" mt={1}>
                {signinError}
              </Typography>
            )}
          </Box>
          <Box sx={buttonsContainer}>
            <Button sx={button} color="info" variant="contained" type="submit">
              {constants.SIGNIN_BTN}
            </Button>
          </Box>
        </Box>
      </form>
    </Container>
  );
};

export default SignIn;
